// src/components/events/EventCreationForm.js
// Multi-section form for creating and editing events.
// All sections visible on one scrollable page with clear section headers.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { auth, db } from '../../config/firebase-config';
import { doc, getDoc } from 'firebase/firestore';
import { getIntakeForms } from '../../services/intake-form-service';
import { createEvent, updateEvent, publishEvent, getDefaultEventData } from '../../services/event-service';
import { uploadSiteImage } from '../../services/image-upload-service';
import { getZoomStatus } from '../../services/zoom-service';
import SimpleRichEditor from '../common/SimpleRichEditor';
import { ArrowLeft, X, Calendar, MapPin, Users, DollarSign, FileText, Bell, MessageSquare, Loader2, Image } from 'lucide-react';
import { trackEvent } from '../../utils/analytics';

// ─── Constants ──────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
];

const DAY_MAP = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };

// ─── Helpers ────────────────────────────────────────────────────────────────

function generateSessionDates(startDate, recurringDay, sessionCount, excludedDates) {
  if (!startDate || !recurringDay || !sessionCount) return [];
  const targetDay = DAY_MAP[recurringDay.toLowerCase()];
  if (targetDay === undefined) return [];

  const dates = [];
  const excludedSet = new Set((excludedDates || []).map(d => d.split('T')[0]));
  const current = new Date(startDate + 'T12:00:00');

  // Find first occurrence of target day
  while (current.getDay() !== targetDay) {
    current.setDate(current.getDate() + 1);
  }

  let added = 0;
  while (added < sessionCount) {
    const dateStr = current.toISOString().split('T')[0];
    if (!excludedSet.has(dateStr)) {
      dates.push(dateStr);
      added++;
    }
    current.setDate(current.getDate() + 7);
  }
  return dates;
}

function generateDailySchedule(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const days = [];
  const current = new Date(startDate + 'T12:00:00');
  const end = new Date(endDate + 'T12:00:00');
  while (current <= end) {
    days.push({
      date: current.toISOString().split('T')[0],
      startTime: '09:00',
      endTime: '17:00',
    });
    current.setDate(current.getDate() + 1);
  }
  return days;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function formatTime12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function dollarsToCents(dollars) {
  if (!dollars && dollars !== 0) return null;
  return Math.round(parseFloat(dollars) * 100);
}

function centsToDollars(cents) {
  if (!cents && cents !== 0) return '';
  return (cents / 100).toFixed(2);
}

// ─── Section Header ─────────────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
      {Icon && <Icon size={20} color="#7c3aed" />}
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0, fontFamily: 'Montserrat, sans-serif' }}>{title}</h3>
    </div>
    {subtitle && <p style={{ fontSize: 13, color: '#6B7280', margin: 0, marginLeft: Icon ? 30 : 0 }}>{subtitle}</p>}
  </div>
);

// ─── Form Field Wrappers ────────────────────────────────────────────────────

const fieldLabelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600, color: '#374151',
  marginBottom: 6, fontFamily: 'Montserrat, sans-serif',
};
const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8,
  fontSize: 14, fontFamily: 'Montserrat, sans-serif', outline: 'none',
  transition: 'border-color 0.15s', boxSizing: 'border-box',
};
const sectionCardStyle = {
  background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 12,
  padding: 24, marginBottom: 24,
};

// ─── Main Component ─────────────────────────────────────────────────────────

const EventCreationForm = ({ existingEvent, onClose, onSaved }) => {
  const isEditing = !!existingEvent;

  // ── Form State ──
  const [form, setForm] = useState(() => {
    if (existingEvent) {
      return {
        ...getDefaultEventData(),
        ...existingEvent,
        // Convert cents back to dollars for price fields
        pricing: {
          ...getDefaultEventData().pricing,
          ...existingEvent.pricing,
          price: existingEvent.pricing?.price ? centsToDollars(existingEvent.pricing.price) : '',
          earlyBirdPrice: existingEvent.pricing?.earlyBirdPrice ? centsToDollars(existingEvent.pricing.earlyBirdPrice) : '',
          depositAmount: existingEvent.pricing?.depositAmount ? centsToDollars(existingEvent.pricing.depositAmount) : '',
          totalPrice: existingEvent.pricing?.totalPrice ? centsToDollars(existingEvent.pricing.totalPrice) : '',
        },
      };
    }
    return { ...getDefaultEventData(), pricing: { ...getDefaultEventData().pricing, price: '', earlyBirdPrice: '', depositAmount: '', totalPrice: '' } };
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [practitionerModalities, setPractitionerModalities] = useState([]);
  const [intakeForms, setIntakeForms] = useState([]);
  const [zoomConnected, setZoomConnected] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(existingEvent?.coverImage || null);
  const [deadlineMode, setDeadlineMode] = useState(existingEvent?.registrationDeadline ? 'custom' : 'auto');

  // ── Load practitioner data ──
  useEffect(() => {
    const uid = auth?.currentUser?.uid;
    if (!uid) return;

    // Load modalities
    (async () => {
      try {
        const practDoc = await getDoc(doc(db, 'practitioners', uid));
        if (practDoc.exists()) {
          const data = practDoc.data();
          const mods = data.modalities?.selected || [];
          const customs = data.modalities?.custom || [];
          const allMods = [...mods.map(m => m.name || m.label || m), ...customs.map(m => m.name || m.label || m)];
          setPractitionerModalities(allMods.filter(Boolean));

          // Set timezone from practitioner settings if available
          if (!isEditing && data.timezone) {
            setForm(prev => ({ ...prev, timezone: data.timezone }));
          }
        }
      } catch (err) {
        console.warn('Failed to load modalities:', err.message);
      }
    })();

    // Load intake forms
    getIntakeForms(uid).then(forms => setIntakeForms(forms)).catch(() => {});

    // Check Zoom status
    getZoomStatus(uid).then(status => {
      if (status?.connected) setZoomConnected(true);
    }).catch(() => {});
  }, [isEditing]);

  // ── Auto-generate session dates for series ──
  const sessionDates = useMemo(() => {
    if (form.eventType !== 'series') return [];
    return generateSessionDates(
      form.schedule.startDate,
      form.schedule.recurringDay,
      form.schedule.sessionCount,
      form.schedule.excludedDates,
    );
  }, [form.eventType, form.schedule.startDate, form.schedule.recurringDay, form.schedule.sessionCount, form.schedule.excludedDates]);

  // ── All possible dates (before exclusions) for the checkbox list ──
  const allPossibleDates = useMemo(() => {
    if (form.eventType !== 'series' || !form.schedule.startDate || !form.schedule.recurringDay || !form.schedule.sessionCount) return [];
    const targetDay = DAY_MAP[form.schedule.recurringDay.toLowerCase()];
    if (targetDay === undefined) return [];

    const dates = [];
    const current = new Date(form.schedule.startDate + 'T12:00:00');
    while (current.getDay() !== targetDay) {
      current.setDate(current.getDate() + 1);
    }
    // Generate enough dates to cover sessionCount + potential exclusions
    const maxDates = (form.schedule.sessionCount || 4) + 8;
    for (let i = 0; i < maxDates; i++) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 7);
    }
    return dates;
  }, [form.eventType, form.schedule.startDate, form.schedule.recurringDay, form.schedule.sessionCount]);

  // ── Auto-generate daily schedule for multi-day ──
  useEffect(() => {
    if (form.eventType === 'multi_day' && form.schedule.date && form.schedule.endDate) {
      const existing = form.schedule.dailySchedule;
      const newDays = generateDailySchedule(form.schedule.date, form.schedule.endDate);
      // Preserve existing times if dates match
      const merged = newDays.map(day => {
        const found = existing?.find(e => e.date === day.date);
        return found || day;
      });
      setForm(prev => ({ ...prev, schedule: { ...prev.schedule, dailySchedule: merged } }));
    }
  }, [form.eventType, form.schedule.date, form.schedule.endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Form updaters ──
  const updateField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateSchedule = useCallback((field, value) => {
    setForm(prev => ({ ...prev, schedule: { ...prev.schedule, [field]: value } }));
  }, []);

  const updatePricing = useCallback((field, value) => {
    setForm(prev => ({ ...prev, pricing: { ...prev.pricing, [field]: value } }));
  }, []);

  const updateReminder = useCallback((field, value) => {
    setForm(prev => ({ ...prev, reminders: { ...prev.reminders, [field]: value } }));
  }, []);

  const updateVenue = useCallback((field, value) => {
    setForm(prev => ({
      ...prev,
      venue: { ...(prev.venue || { name: '', address: '', directions: '' }), [field]: value },
    }));
  }, []);

  const updateDailySchedule = useCallback((index, field, value) => {
    setForm(prev => {
      const updated = [...(prev.schedule.dailySchedule || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, schedule: { ...prev.schedule, dailySchedule: updated } };
    });
  }, []);

  const toggleExcludedDate = useCallback((dateStr) => {
    setForm(prev => {
      const current = prev.schedule.excludedDates || [];
      const next = current.includes(dateStr)
        ? current.filter(d => d !== dateStr)
        : [...current, dateStr];
      return { ...prev, schedule: { ...prev.schedule, excludedDates: next } };
    });
  }, []);

  const toggleModality = useCallback((mod) => {
    setForm(prev => {
      const current = prev.modalities || [];
      const next = current.includes(mod)
        ? current.filter(m => m !== mod)
        : [...current, mod];
      return { ...prev, modalities: next };
    });
  }, []);

  // ── Cover image handler ──
  const handleCoverImageSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('File must be an image'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be less than 5MB'); return; }
    setCoverImageFile(file);
    setCoverImagePreview(URL.createObjectURL(file));
  }, []);

  const removeCoverImage = useCallback(() => {
    setCoverImageFile(null);
    setCoverImagePreview(null);
    updateField('coverImage', null);
  }, [updateField]);

  // ── Save handler ──
  const handleSave = useCallback(async (shouldPublish) => {
    setError('');
    setSaving(true);

    try {
      const uid = auth?.currentUser?.uid;
      if (!uid) throw new Error('You must be logged in');

      // Upload cover image if new file selected
      let coverImageUrl = form.coverImage;
      if (coverImageFile) {
        const result = await uploadSiteImage(coverImageFile, 'event-covers', uid);
        if (result.success) {
          coverImageUrl = result.url;
        } else {
          console.warn('Cover image upload failed:', result.error);
        }
      }

      // Build payload — convert dollar amounts to cents
      const payload = {
        title: form.title,
        description: form.description,
        eventType: form.eventType,
        coverImage: coverImageUrl,
        modalities: form.modalities,
        schedule: form.schedule,
        timezone: form.timezone,
        format: form.format,
        venue: (form.format === 'in_person' || form.format === 'hybrid') ? form.venue : null,
        manualMeetingLink: form.manualMeetingLink || null,
        maxAttendees: parseInt(form.maxAttendees, 10) || 20,
        pricingModel: form.pricingModel,
        pricing: {
          price: dollarsToCents(form.pricing.price),
          earlyBirdPrice: dollarsToCents(form.pricing.earlyBirdPrice),
          earlyBirdDeadline: form.pricing.earlyBirdDeadline || null,
          depositAmount: dollarsToCents(form.pricing.depositAmount),
          totalPrice: dollarsToCents(form.pricing.totalPrice),
          remainderDueDays: form.pricing.remainderDueDays ? parseInt(form.pricing.remainderDueDays, 10) : null,
        },
        registrationDeadline: deadlineMode === 'custom' ? form.registrationDeadline : null,
        intakeFormId: form.intakeFormId || null,
        reminders: form.reminders,
        thankYouMessage: form.thankYouMessage || null,
      };

      if (isEditing) {
        await updateEvent(existingEvent.id, payload);
        if (shouldPublish && existingEvent.status === 'draft') {
          await publishEvent(existingEvent.id);
        }
      } else {
        payload.publishImmediately = shouldPublish;
        await createEvent(payload);
      }

      if (!isEditing) {
        trackEvent('event_created', { event_type: form.eventType, pricing_model: form.pricingModel, format: form.format });
      }
      onSaved?.();
    } catch (err) {
      console.error('Save event error:', err);
      setError(err.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  }, [form, coverImageFile, deadlineMode, isEditing, existingEvent, onSaved]);

  // ── Render ──
  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '16px 24px', position: 'sticky', top: 0, zIndex: 40 }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              role="button"
              tabIndex={0}
              onClick={onClose}
              onKeyDown={(e) => e.key === 'Enter' && onClose?.()}
              style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: 14, fontFamily: 'Montserrat, sans-serif' }}
            >
              <ArrowLeft size={16} />
              Back
            </span>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0, fontFamily: 'Montserrat, sans-serif' }}>
              {isEditing ? 'Edit Event' : 'Create Event'}
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={() => handleSave(false)}
              disabled={saving}
              style={{
                padding: '8px 20px', borderRadius: 8, border: '1px solid #D1D5DB',
                background: '#FFFFFF', color: '#374151', fontWeight: 600, fontSize: 14,
                fontFamily: 'Montserrat, sans-serif', cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {isEditing && existingEvent.status !== 'draft' ? 'Save changes' : 'Save as draft'}
            </button>
            {(!isEditing || existingEvent.status === 'draft') && (
              <button
                type="button"
                onClick={() => handleSave(true)}
                disabled={saving}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none',
                  background: '#7c3aed', color: '#FFFFFF', fontWeight: 600, fontSize: 14,
                  fontFamily: 'Montserrat, sans-serif', cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Save &amp; publish
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ maxWidth: 800, margin: '16px auto 0', padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, color: '#DC2626', fontSize: 14, fontFamily: 'Montserrat, sans-serif' }}>
          {error}
        </div>
      )}

      {/* Form body */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px 80px' }}>

        {/* ──────────── Section 1: Basic Information ──────────── */}
        <div style={sectionCardStyle}>
          <SectionHeader icon={FileText} title="Basic Information" />

          {/* Title */}
          <div style={{ marginBottom: 16 }}>
            <label style={fieldLabelStyle}>Event title <span style={{ color: '#DC2626' }}>*</span></label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="e.g., Full Moon Meditation Circle"
              style={inputStyle}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <label style={fieldLabelStyle}>Description <span style={{ color: '#DC2626' }}>*</span></label>
            <SimpleRichEditor
              value={form.description}
              onChange={(val) => updateField('description', val)}
              placeholder="Describe your event — what participants will experience, learn, or gain..."
            />
          </div>

          {/* Event Type */}
          <div style={{ marginBottom: 16 }}>
            <label style={fieldLabelStyle}>Event type <span style={{ color: '#DC2626' }}>*</span></label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                { value: 'single', label: 'Single session' },
                { value: 'series', label: 'Multi-session series' },
                { value: 'multi_day', label: 'Multi-day event' },
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontFamily: 'Montserrat, sans-serif', color: '#374151' }}>
                  <input
                    type="radio"
                    name="eventType"
                    value={opt.value}
                    checked={form.eventType === opt.value}
                    onChange={(e) => updateField('eventType', e.target.value)}
                    style={{ accentColor: '#7c3aed' }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Cover Image */}
          <div style={{ marginBottom: 16 }}>
            <label style={fieldLabelStyle}>Cover image</label>
            {coverImagePreview ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={coverImagePreview}
                  alt="Cover preview"
                  style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E7EB' }}
                />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={removeCoverImage}
                  onKeyDown={(e) => e.key === 'Enter' && removeCoverImage()}
                  style={{
                    position: 'absolute', top: -8, right: -8, width: 24, height: 24,
                    background: '#DC2626', color: '#FFF', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  <X size={14} />
                </span>
              </div>
            ) : (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
                border: '2px dashed #D1D5DB', borderRadius: 8, cursor: 'pointer',
                color: '#6B7280', fontSize: 14, fontFamily: 'Montserrat, sans-serif',
                width: 'fit-content',
              }}>
                <Image size={18} />
                Upload image
                <input type="file" accept="image/*" onChange={handleCoverImageSelect} style={{ display: 'none' }} />
              </label>
            )}
            <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Recommended, not required. Max 5MB.</p>
          </div>

          {/* Modalities / Tags */}
          {practitionerModalities.length > 0 && (
            <div>
              <label style={fieldLabelStyle}>Categories / Tags</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {practitionerModalities.map(mod => {
                  const isSelected = form.modalities.includes(mod);
                  return (
                    <span
                      key={mod}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleModality(mod)}
                      onKeyDown={(e) => e.key === 'Enter' && toggleModality(mod)}
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500,
                        fontFamily: 'Montserrat, sans-serif', cursor: 'pointer', userSelect: 'none',
                        border: isSelected ? '1px solid #7c3aed' : '1px solid #D1D5DB',
                        background: isSelected ? '#F3F0FF' : '#FFFFFF',
                        color: isSelected ? '#7c3aed' : '#6B7280',
                        transition: 'all 0.15s',
                      }}
                    >
                      {mod}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ──────────── Section 2: Scheduling ──────────── */}
        <div style={sectionCardStyle}>
          <SectionHeader icon={Calendar} title="Scheduling" subtitle={
            form.eventType === 'single' ? 'Set the date and time for your event' :
            form.eventType === 'series' ? 'Configure your recurring session schedule' :
            'Set up your multi-day event schedule'
          } />

          {/* Single session */}
          {form.eventType === 'single' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <label style={fieldLabelStyle}>Date <span style={{ color: '#DC2626' }}>*</span></label>
                <input type="date" value={form.schedule.date || ''} onChange={(e) => updateSchedule('date', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={fieldLabelStyle}>Start time <span style={{ color: '#DC2626' }}>*</span></label>
                <input type="time" value={form.schedule.startTime || ''} onChange={(e) => updateSchedule('startTime', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={fieldLabelStyle}>End time <span style={{ color: '#DC2626' }}>*</span></label>
                <input type="time" value={form.schedule.endTime || ''} onChange={(e) => updateSchedule('endTime', e.target.value)} style={inputStyle} />
              </div>
            </div>
          )}

          {/* Multi-session series */}
          {form.eventType === 'series' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={fieldLabelStyle}>Start date <span style={{ color: '#DC2626' }}>*</span></label>
                  <input type="date" value={form.schedule.startDate || ''} onChange={(e) => updateSchedule('startDate', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={fieldLabelStyle}>Day of week <span style={{ color: '#DC2626' }}>*</span></label>
                  <select value={form.schedule.recurringDay || ''} onChange={(e) => updateSchedule('recurringDay', e.target.value)} style={inputStyle}>
                    <option value="">Select day...</option>
                    {DAYS_OF_WEEK.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={fieldLabelStyle}>Time <span style={{ color: '#DC2626' }}>*</span></label>
                  <input type="time" value={form.schedule.recurringTime || ''} onChange={(e) => updateSchedule('recurringTime', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={fieldLabelStyle}>Duration (minutes) <span style={{ color: '#DC2626' }}>*</span></label>
                  <input type="number" min={15} step={15} value={form.schedule.sessionDuration || ''} onChange={(e) => updateSchedule('sessionDuration', parseInt(e.target.value, 10) || null)} style={inputStyle} />
                </div>
                <div>
                  <label style={fieldLabelStyle}>Number of sessions <span style={{ color: '#DC2626' }}>*</span></label>
                  <input type="number" min={2} max={52} value={form.schedule.sessionCount || ''} onChange={(e) => updateSchedule('sessionCount', parseInt(e.target.value, 10) || null)} style={inputStyle} />
                </div>
              </div>

              {/* Skip dates */}
              {allPossibleDates.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <label style={fieldLabelStyle}>Skip any dates? (optional)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {allPossibleDates.map(dateStr => {
                      const isExcluded = (form.schedule.excludedDates || []).includes(dateStr);
                      const d = new Date(dateStr + 'T12:00:00');
                      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      return (
                        <label key={dateStr} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontFamily: 'Montserrat, sans-serif', color: isExcluded ? '#DC2626' : '#374151', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={isExcluded}
                            onChange={() => toggleExcludedDate(dateStr)}
                            style={{ accentColor: '#7c3aed' }}
                          />
                          {label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Generated session list */}
              {sessionDates.length > 0 && (
                <div>
                  <label style={fieldLabelStyle}>Generated sessions:</label>
                  <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 12 }}>
                    {sessionDates.map((dateStr, i) => {
                      const d = new Date(dateStr + 'T12:00:00');
                      const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                      const start = formatTime12(form.schedule.recurringTime);
                      const endMinutes = (parseInt(form.schedule.recurringTime?.split(':')[0], 10) * 60 + parseInt(form.schedule.recurringTime?.split(':')[1], 10) + (form.schedule.sessionDuration || 0));
                      const endH = Math.floor(endMinutes / 60);
                      const endM = endMinutes % 60;
                      const end = formatTime12(`${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`);
                      return (
                        <div key={dateStr} style={{ padding: '4px 0', fontSize: 14, fontFamily: 'Montserrat, sans-serif', color: '#374151' }}>
                          {i + 1}. {dayLabel}, {start} — {end}
                        </div>
                      );
                    })}
                    {(form.schedule.excludedDates || []).length > 0 && (
                      <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8, fontStyle: 'italic' }}>
                        {form.schedule.excludedDates.length} date(s) skipped
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Multi-day */}
          {form.eventType === 'multi_day' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={fieldLabelStyle}>Start date <span style={{ color: '#DC2626' }}>*</span></label>
                  <input type="date" value={form.schedule.date || ''} onChange={(e) => updateSchedule('date', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={fieldLabelStyle}>End date <span style={{ color: '#DC2626' }}>*</span></label>
                  <input type="date" value={form.schedule.endDate || ''} onChange={(e) => updateSchedule('endDate', e.target.value)} style={inputStyle} />
                </div>
              </div>

              {/* Daily schedule */}
              {form.schedule.dailySchedule?.length > 0 && (
                <div>
                  <label style={fieldLabelStyle}>Daily schedule:</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {form.schedule.dailySchedule.map((day, i) => (
                      <div key={day.date} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: 12, alignItems: 'center' }}>
                        <span style={{ fontSize: 14, fontWeight: 500, fontFamily: 'Montserrat, sans-serif', color: '#374151' }}>
                          {formatDate(day.date)}
                        </span>
                        <input
                          type="time"
                          value={day.startTime}
                          onChange={(e) => updateDailySchedule(i, 'startTime', e.target.value)}
                          style={{ ...inputStyle, padding: '8px 10px' }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#9CA3AF', fontSize: 14 }}>to</span>
                          <input
                            type="time"
                            value={day.endTime}
                            onChange={(e) => updateDailySchedule(i, 'endTime', e.target.value)}
                            style={{ ...inputStyle, padding: '8px 10px' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Timezone */}
          <div style={{ marginTop: 16 }}>
            <label style={fieldLabelStyle}>Timezone</label>
            <select value={form.timezone || 'America/Los_Angeles'} onChange={(e) => updateField('timezone', e.target.value)} style={inputStyle}>
              <optgroup label="US Timezones">
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
              </optgroup>
              <optgroup label="Other">
                <option value="America/Phoenix">Arizona (no DST)</option>
                <option value="America/Toronto">Eastern (Canada)</option>
                <option value="America/Vancouver">Pacific (Canada)</option>
                <option value="Europe/London">London (GMT/BST)</option>
                <option value="Europe/Paris">Central European (CET)</option>
                <option value="Asia/Tokyo">Japan (JST)</option>
                <option value="Australia/Sydney">Sydney (AEST)</option>
              </optgroup>
            </select>
          </div>
        </div>

        {/* ──────────── Section 3: Format & Location ──────────── */}
        <div style={sectionCardStyle}>
          <SectionHeader icon={MapPin} title="Format & Location" />

          <div style={{ marginBottom: 16 }}>
            <label style={fieldLabelStyle}>Format <span style={{ color: '#DC2626' }}>*</span></label>
            <div style={{ display: 'flex', gap: 12 }}>
              {[
                { value: 'virtual', label: 'Virtual' },
                { value: 'in_person', label: 'In-person' },
                { value: 'hybrid', label: 'Hybrid' },
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontFamily: 'Montserrat, sans-serif', color: '#374151' }}>
                  <input
                    type="radio"
                    name="format"
                    value={opt.value}
                    checked={form.format === opt.value}
                    onChange={(e) => updateField('format', e.target.value)}
                    style={{ accentColor: '#7c3aed' }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Virtual meeting link */}
          {(form.format === 'virtual' || form.format === 'hybrid') && (
            <div style={{ marginBottom: 16 }}>
              {zoomConnected ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#F0FDF4', borderRadius: 8, border: '1px solid #BBF7D0' }}>
                  <span style={{ color: '#16A34A', fontSize: 14 }}>&#10003;</span>
                  <span style={{ fontSize: 14, fontFamily: 'Montserrat, sans-serif', color: '#15803D' }}>Zoom meeting will be auto-generated</span>
                </div>
              ) : (
                <div>
                  <label style={fieldLabelStyle}>Meeting link (optional)</label>
                  <input
                    type="url"
                    value={form.manualMeetingLink || ''}
                    onChange={(e) => updateField('manualMeetingLink', e.target.value)}
                    placeholder="https://zoom.us/j/... or Google Meet link"
                    style={inputStyle}
                  />
                </div>
              )}
            </div>
          )}

          {/* Venue fields */}
          {(form.format === 'in_person' || form.format === 'hybrid') && (
            <div>
              <div style={{ marginBottom: 12 }}>
                <label style={fieldLabelStyle}>Venue name <span style={{ color: '#DC2626' }}>*</span></label>
                <input type="text" value={form.venue?.name || ''} onChange={(e) => updateVenue('name', e.target.value)} placeholder="e.g., Downtown Yoga Studio" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={fieldLabelStyle}>Address <span style={{ color: '#DC2626' }}>*</span></label>
                <input type="text" value={form.venue?.address || ''} onChange={(e) => updateVenue('address', e.target.value)} placeholder="123 Main St, City, State" style={inputStyle} />
              </div>
              <div>
                <label style={fieldLabelStyle}>Directions / Parking notes (optional)</label>
                <textarea
                  value={form.venue?.directions || ''}
                  onChange={(e) => updateVenue('directions', e.target.value)}
                  placeholder="e.g., Free parking in rear lot. Enter through glass doors on Oak Street."
                  rows={2}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* ──────────── Section 4: Capacity ──────────── */}
        <div style={sectionCardStyle}>
          <SectionHeader icon={Users} title="Capacity" />
          <div style={{ maxWidth: 200 }}>
            <label style={fieldLabelStyle}>Maximum attendees <span style={{ color: '#DC2626' }}>*</span></label>
            <input
              type="number"
              min={1}
              max={1000}
              value={form.maxAttendees}
              onChange={(e) => updateField('maxAttendees', parseInt(e.target.value, 10) || '')}
              style={inputStyle}
            />
          </div>
        </div>

        {/* ──────────── Section 5: Pricing ──────────── */}
        <div style={sectionCardStyle}>
          <SectionHeader icon={DollarSign} title="Pricing" />

          <div style={{ marginBottom: 16 }}>
            <label style={fieldLabelStyle}>Pricing model <span style={{ color: '#DC2626' }}>*</span></label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                { value: 'flat', label: 'Flat rate' },
                { value: 'early_bird', label: 'Early bird' },
                { value: 'deposit', label: 'Deposit' },
                { value: 'free', label: 'Free' },
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontFamily: 'Montserrat, sans-serif', color: '#374151' }}>
                  <input
                    type="radio"
                    name="pricingModel"
                    value={opt.value}
                    checked={form.pricingModel === opt.value}
                    onChange={(e) => updateField('pricingModel', e.target.value)}
                    style={{ accentColor: '#7c3aed' }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Flat rate */}
          {form.pricingModel === 'flat' && (
            <div style={{ maxWidth: 250 }}>
              <label style={fieldLabelStyle}>Price per attendee <span style={{ color: '#DC2626' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 14, fontWeight: 600 }}>$</span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.pricing.price}
                  onChange={(e) => updatePricing('price', e.target.value)}
                  placeholder="75.00"
                  style={{ ...inputStyle, paddingLeft: 28 }}
                />
              </div>
            </div>
          )}

          {/* Early bird */}
          {form.pricingModel === 'early_bird' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <label style={fieldLabelStyle}>Early bird price <span style={{ color: '#DC2626' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 14, fontWeight: 600 }}>$</span>
                  <input type="number" min={0} step={0.01} value={form.pricing.earlyBirdPrice} onChange={(e) => updatePricing('earlyBirdPrice', e.target.value)} placeholder="55.00" style={{ ...inputStyle, paddingLeft: 28 }} />
                </div>
              </div>
              <div>
                <label style={fieldLabelStyle}>Until <span style={{ color: '#DC2626' }}>*</span></label>
                <input type="date" value={form.pricing.earlyBirdDeadline || ''} onChange={(e) => updatePricing('earlyBirdDeadline', e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={fieldLabelStyle}>Regular price <span style={{ color: '#DC2626' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 14, fontWeight: 600 }}>$</span>
                  <input type="number" min={0} step={0.01} value={form.pricing.price} onChange={(e) => updatePricing('price', e.target.value)} placeholder="75.00" style={{ ...inputStyle, paddingLeft: 28 }} />
                </div>
              </div>
            </div>
          )}

          {/* Deposit */}
          {form.pricingModel === 'deposit' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <label style={fieldLabelStyle}>Deposit amount <span style={{ color: '#DC2626' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 14, fontWeight: 600 }}>$</span>
                  <input type="number" min={0} step={0.01} value={form.pricing.depositAmount} onChange={(e) => updatePricing('depositAmount', e.target.value)} placeholder="50.00" style={{ ...inputStyle, paddingLeft: 28 }} />
                </div>
              </div>
              <div>
                <label style={fieldLabelStyle}>Total price <span style={{ color: '#DC2626' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 14, fontWeight: 600 }}>$</span>
                  <input type="number" min={0} step={0.01} value={form.pricing.totalPrice} onChange={(e) => updatePricing('totalPrice', e.target.value)} placeholder="200.00" style={{ ...inputStyle, paddingLeft: 28 }} />
                </div>
              </div>
              <div>
                <label style={fieldLabelStyle}>Remainder due <span style={{ color: '#DC2626' }}>*</span></label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="number" min={1} max={90} value={form.pricing.remainderDueDays || ''} onChange={(e) => updatePricing('remainderDueDays', e.target.value)} placeholder="7" style={{ ...inputStyle, width: 80 }} />
                  <span style={{ fontSize: 13, color: '#6B7280', fontFamily: 'Montserrat, sans-serif', whiteSpace: 'nowrap' }}>days before event</span>
                </div>
              </div>
            </div>
          )}

          {/* Free */}
          {form.pricingModel === 'free' && (
            <p style={{ fontSize: 14, color: '#6B7280', fontFamily: 'Montserrat, sans-serif', fontStyle: 'italic' }}>
              No payment — registration only
            </p>
          )}
        </div>

        {/* ──────────── Section 6: Registration Settings ──────────── */}
        <div style={sectionCardStyle}>
          <SectionHeader icon={FileText} title="Registration Settings" />

          <div style={{ marginBottom: 16 }}>
            <label style={fieldLabelStyle}>Registration deadline</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontFamily: 'Montserrat, sans-serif', color: '#374151' }}>
                <input type="radio" name="deadlineMode" value="auto" checked={deadlineMode === 'auto'} onChange={() => setDeadlineMode('auto')} style={{ accentColor: '#7c3aed' }} />
                Auto-close at event time (default)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontFamily: 'Montserrat, sans-serif', color: '#374151' }}>
                <input type="radio" name="deadlineMode" value="custom" checked={deadlineMode === 'custom'} onChange={() => setDeadlineMode('custom')} style={{ accentColor: '#7c3aed' }} />
                Custom deadline:
              </label>
              {deadlineMode === 'custom' && (
                <input
                  type="datetime-local"
                  value={form.registrationDeadline || ''}
                  onChange={(e) => updateField('registrationDeadline', e.target.value)}
                  style={{ ...inputStyle, maxWidth: 300, marginLeft: 24 }}
                />
              )}
            </div>
          </div>

          <div>
            <label style={fieldLabelStyle}>Attach intake form (optional)</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <select
                value={form.intakeFormId || ''}
                onChange={(e) => updateField('intakeFormId', e.target.value || null)}
                style={{ ...inputStyle, maxWidth: 300 }}
              >
                <option value="">None</option>
                {intakeForms.map(f => (
                  <option key={f.id} value={f.id}>{f.title}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ──────────── Section 7: Reminders ──────────── */}
        <div style={sectionCardStyle}>
          <SectionHeader icon={Bell} title="Reminders" subtitle="Send reminders to attendees" />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { key: 'oneWeek', label: '1 week before' },
              { key: 'threeDays', label: '3 days before' },
              { key: 'twentyFourHours', label: '24 hours before' },
              { key: 'twoHours', label: '2 hours before' },
            ].map(opt => (
              <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontFamily: 'Montserrat, sans-serif', color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={form.reminders[opt.key]}
                  onChange={(e) => updateReminder(opt.key, e.target.checked)}
                  style={{ accentColor: '#7c3aed', width: 16, height: 16 }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {/* ──────────── Section 8: Post-Event ──────────── */}
        <div style={sectionCardStyle}>
          <SectionHeader icon={MessageSquare} title="Post-Event" subtitle="Optional" />

          <div>
            <label style={fieldLabelStyle}>Custom thank-you message (optional)</label>
            <textarea
              value={form.thankYouMessage || ''}
              onChange={(e) => updateField('thankYouMessage', e.target.value)}
              placeholder="Thank you for attending! We hope you enjoyed the experience."
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>
              Default: &ldquo;Thank you for attending {form.title || '[Event]'}!&rdquo;
            </p>
          </div>
        </div>

        {/* ──────────── Bottom action bar (mobile-friendly) ──────────── */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={saving}
            style={{
              padding: '10px 24px', borderRadius: 8, border: '1px solid #D1D5DB',
              background: '#FFFFFF', color: '#374151', fontWeight: 600, fontSize: 14,
              fontFamily: 'Montserrat, sans-serif', cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {isEditing && existingEvent.status !== 'draft' ? 'Save changes' : 'Save as draft'}
          </button>
          {(!isEditing || existingEvent.status === 'draft') && (
            <button
              type="button"
              onClick={() => handleSave(true)}
              disabled={saving}
              style={{
                padding: '10px 24px', borderRadius: 8, border: 'none',
                background: '#7c3aed', color: '#FFFFFF', fontWeight: 600, fontSize: 14,
                fontFamily: 'Montserrat, sans-serif', cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              Save &amp; publish
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCreationForm;
